<template>
  <div id="printable" class="report-container">
    <!-- Header -->
    <div class="header">
      <div class="facility-name">
        TUURU COTTOLENGO HEALTH CENTRE - AR2026259
      </div>
      <div class="facility-details">
        <div>Igembe Central</div>
        <div>P.O BOX 77 - Meru | Tel: 254254746758069</div>
        <div>Email: suore.tuuru@cottolengo.org</div>
      </div>
      <div class="report-title">MOH 505 - IDSR WEEKLY REPORT</div>
      <div class="report-subtitle">
        Integrated Disease Surveillance and Response
      </div>
    </div>

    <!-- Week Selection and Generated By Bar -->
    <div class="info-bar">
      <div class="info-left">
        <div class="info-item">
          <span class="info-label">Epidemiological Week:</span>
          <select v-model="selectedWeek" class="week-select">
            <option v-for="week in weeks" :key="week" :value="week">
              Week {{ week }}
            </option>
          </select>
        </div>
        <div class="info-item">
          <span class="info-label">Year:</span>
          <input
            type="number"
            v-model="selectedYear"
            class="year-input"
            min="2020"
            max="2030"
          />
        </div>
        <div class="info-item">
          <span class="info-label">Reporting Date:</span>
          <span class="info-value">{{ reportingDate }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Generated By:</span>
          <span class="info-value">{{ generatedBy }}</span>
        </div>
      </div>
      <button @click="printPage" class="btn-print">Print Report</button>
    </div>

    <!-- Period Display for Print -->
    <div class="print-period">
      <strong>Reporting Period:</strong> Week {{ selectedWeek }} ({{
        weekStartDate
      }}
      to {{ weekEndDate }}), {{ selectedYear }}
    </div>

    <!-- Summary Statistics -->
    <div class="summary-stats">
      <div class="stat-box">
        <span class="stat-label">Total Alert Diseases</span>
        <span class="stat-number">{{ totalAlerts }}</span>
      </div>
      <div class="stat-box">
        <span class="stat-label">Confirmed Cases</span>
        <span class="stat-number">{{ totalConfirmed }}</span>
      </div>
      <div class="stat-box">
        <span class="stat-label">Deaths Reported</span>
        <span class="stat-number">{{ totalDeaths }}</span>
      </div>
      <div class="stat-box">
        <span class="stat-label">Outbreaks Detected</span>
        <span class="stat-number">{{ totalOutbreaks }}</span>
      </div>
    </div>

    <!-- Section 1: Immediately Notifiable Diseases -->
    <div class="section">
      <div class="section-title">
        1. IMMEDIATELY NOTIFIABLE DISEASES (IDSR Priority Diseases)
      </div>
      <div class="table-wrapper">
        <table class="idsr-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Disease / Condition</th>
              <th>Suspected Cases</th>
              <th>Confirmed Cases</th>
              <th>Deaths</th>
              <th>Alert Threshold</th>
              <th>Alert Triggered</th>
              <th>Action Taken</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(disease, index) in immediatelyNotifiable" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ disease.name }}</td>
              <td class="text-center">{{ disease.suspected }}</td>
              <td class="text-center">{{ disease.confirmed }}</td>
              <td class="text-center">{{ disease.deaths }}</td>
              <td class="text-center">{{ disease.threshold }}</td>
              <td class="text-center">
                <span v-if="disease.alertTriggered" class="alert-badge"
                  >Yes</span
                >
                <span v-else class="no-alert-badge">No</span>
              </td>
              <td class="text-center">{{ disease.actionTaken }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 2: Epidemic Prone Diseases -->
    <div class="section">
      <div class="section-title">2. EPIDEMIC PRONE DISEASES</div>
      <div class="table-wrapper">
        <table class="idsr-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Disease / Condition</th>
              <th>Suspected Cases</th>
              <th>Confirmed Cases</th>
              <th>Deaths</th>
              <th>Alert Threshold</th>
              <th>Alert Triggered</th>
              <th>Action Taken</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(disease, index) in epidemicProne" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ disease.name }}</td>
              <td class="text-center">{{ disease.suspected }}</td>
              <td class="text-center">{{ disease.confirmed }}</td>
              <td class="text-center">{{ disease.deaths }}</td>
              <td class="text-center">{{ disease.threshold }}</td>
              <td class="text-center">
                <span v-if="disease.alertTriggered" class="alert-badge"
                  >Yes</span
                >
                <span v-else class="no-alert-badge">No</span>
              </td>
              <td class="text-center">{{ disease.actionTaken }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 3: Vaccine Preventable Diseases -->
    <div class="section">
      <div class="section-title">3. VACCINE PREVENTABLE DISEASES</div>
      <div class="table-wrapper">
        <table class="idsr-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Disease / Condition</th>
              <th>Suspected Cases</th>
              <th>Confirmed Cases</th>
              <th>Deaths</th>
              <th>Vaccination Status</th>
              <th>Action Taken</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(disease, index) in vaccinePreventable" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ disease.name }}</td>
              <td class="text-center">{{ disease.suspected }}</td>
              <td class="text-center">{{ disease.confirmed }}</td>
              <td class="text-center">{{ disease.deaths }}</td>
              <td class="text-center">{{ disease.vaccinationStatus }}</td>
              <td class="text-center">{{ disease.actionTaken }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 4: Other Notifiable Conditions -->
    <div class="section">
      <div class="section-title">4. OTHER NOTIFIABLE CONDITIONS</div>
      <div class="table-wrapper">
        <table class="idsr-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Condition</th>
              <th>Number of Cases</th>
              <th>Deaths</th>
              <th>Trend</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(condition, index) in otherConditions" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ condition.name }}</td>
              <td class="text-center">{{ condition.cases }}</td>
              <td class="text-center">{{ condition.deaths }}</td>
              <td class="text-center">
                <span :class="getTrendClass(condition.trend)">{{
                  condition.trend
                }}</span>
              </td>
              <td>{{ condition.remarks }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 5: Maternal and Neonatal Tetanus -->
    <div class="section">
      <div class="section-title">5. MATERNAL AND NEONATAL TETANUS</div>
      <div class="stats-grid-2">
        <div class="stat-card">
          <div class="stat-label">Maternal Tetanus Cases</div>
          <div class="stat-value">{{ maternalTetanus.cases }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Maternal Tetanus Deaths</div>
          <div class="stat-value">{{ maternalTetanus.deaths }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Neonatal Tetanus Cases</div>
          <div class="stat-value">{{ neonatalTetanus.cases }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Neonatal Tetanus Deaths</div>
          <div class="stat-value">{{ neonatalTetanus.deaths }}</div>
        </div>
      </div>
    </div>

    <!-- Section 6: AFP Surveillance -->
    <div class="section">
      <div class="section-title">
        6. ACUTE FLACCID PARALYSIS (AFP) SURVEILLANCE
      </div>
      <div class="stats-grid-2">
        <div class="stat-card">
          <div class="stat-label">AFP Cases Reported</div>
          <div class="stat-value">{{ afp.cases }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Adequate Stool Samples</div>
          <div class="stat-value">{{ afp.adequateStool }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Follow-up at 60 Days</div>
          <div class="stat-value">{{ afp.followUp }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Classification Pending</div>
          <div class="stat-value">{{ afp.pending }}</div>
        </div>
      </div>
    </div>

    <!-- Section 7: Outbreak Summary -->
    <div class="section">
      <div class="section-title">7. OUTBREAK SUMMARY</div>
      <div class="table-wrapper" v-if="outbreaks.length > 0">
        <table class="data-table">
          <thead>
            <tr>
              <th>Disease</th>
              <th>Date of Onset</th>
              <th>Date Reported</th>
              <th>Total Cases</th>
              <th>Total Deaths</th>
              <th>CFR (%)</th>
              <th>Response Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="outbreak in outbreaks" :key="outbreak.disease">
              <td>{{ outbreak.disease }}</td>
              <td class="text-center">{{ outbreak.onsetDate }}</td>
              <td class="text-center">{{ outbreak.reportDate }}</td>
              <td class="text-center">{{ outbreak.cases }}</td>
              <td class="text-center">{{ outbreak.deaths }}</td>
              <td class="text-center">{{ outbreak.cfr }}%</td>
              <td class="text-center">
                <span :class="getResponseClass(outbreak.status)">{{
                  outbreak.status
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="no-data">
        No outbreaks reported during this period.
      </div>
    </div>

    <!-- Section 8: Community Health Strategy -->
    <div class="section">
      <div class="section-title">
        8. COMMUNITY HEALTH STRATEGY (CHS) REPORTING
      </div>
      <div class="stats-grid-3">
        <div class="stat-card">
          <div class="stat-label">Active CHVs</div>
          <div class="stat-value">{{ chs.activeCHVs }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Households Visited</div>
          <div class="stat-value">{{ chs.householdsVisited }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Community Events Reported</div>
          <div class="stat-value">{{ chs.eventsReported }}</div>
        </div>
      </div>
    </div>

    <!-- Section 9: Feedback and Recommendations -->
    <div class="section">
      <div class="section-title">9. FEEDBACK AND RECOMMENDATIONS</div>
      <div class="feedback-box">
        <div class="feedback-item">
          <strong>Feedback from Higher Level:</strong>
          <p>{{ feedback.higherLevel }}</p>
        </div>
        <div class="feedback-item">
          <strong>Recommendations for Next Week:</strong>
          <p>{{ feedback.recommendations }}</p>
        </div>
        <div class="feedback-item">
          <strong>Challenges Encountered:</strong>
          <p>{{ feedback.challenges }}</p>
        </div>
      </div>
    </div>

    <!-- Section 10: Sign Off -->
    <div class="section">
      <div class="section-title">10. REPORT SIGN OFF</div>
      <div class="signoff-grid">
        <div class="signoff-item">
          <span class="signoff-label">Report Compiled By:</span>
          <span class="signoff-value">{{ signOff.compiledBy }}</span>
        </div>
        <div class="signoff-item">
          <span class="signoff-label">Designation:</span>
          <span class="signoff-value">{{ signOff.designation }}</span>
        </div>
        <div class="signoff-item">
          <span class="signoff-label">Signature:</span>
          <span class="signoff-value">{{ signOff.signature }}</span>
        </div>
        <div class="signoff-item">
          <span class="signoff-label">Date:</span>
          <span class="signoff-value">{{ signOff.date }}</span>
        </div>
        <div class="signoff-item">
          <span class="signoff-label">Facility In-Charge:</span>
          <span class="signoff-value">{{ signOff.facilityIncharge }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MOH505IDSR",
  data() {
    return {
      selectedWeek: 16,
      selectedYear: 2026,
      generatedBy: "SR. JANE KAREGI MUNYUGI",
      weeks: Array.from({ length: 52 }, (_, i) => i + 1),

      immediatelyNotifiable: [
        {
          name: "Cholera",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
        {
          name: "Measles",
          suspected: 2,
          confirmed: 1,
          deaths: 0,
          threshold: 1,
          alertTriggered: true,
          actionTaken: "Investigation, sample collected",
        },
        {
          name: "Meningococcal Meningitis",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
        {
          name: "Neonatal Tetanus",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
        {
          name: "Yellow Fever",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
        {
          name: "Viral Hemorrhagic Fevers (Ebola/Marburg)",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
        {
          name: "Anthrax",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
        {
          name: "Plague",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
      ],

      epidemicProne: [
        {
          name: "Malaria",
          suspected: 45,
          confirmed: 38,
          deaths: 1,
          threshold: 30,
          alertTriggered: true,
          actionTaken: "Increased RDT testing",
        },
        {
          name: "Diarrhoea (Bloody)",
          suspected: 8,
          confirmed: 5,
          deaths: 0,
          threshold: 5,
          alertTriggered: true,
          actionTaken: "Water quality assessment",
        },
        {
          name: "Diarrhoea (Watery)",
          suspected: 32,
          confirmed: 28,
          deaths: 0,
          threshold: 20,
          alertTriggered: true,
          actionTaken: "ORS distribution",
        },
        {
          name: "Pneumonia",
          suspected: 25,
          confirmed: 22,
          deaths: 0,
          threshold: 15,
          alertTriggered: true,
          actionTaken: "Antibiotic treatment",
        },
        {
          name: "Leptospirosis",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
        {
          name: "Rift Valley Fever",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
        {
          name: "Dengue Fever",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
        {
          name: "Chikungunya",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          threshold: 1,
          alertTriggered: false,
          actionTaken: "None",
        },
      ],

      vaccinePreventable: [
        {
          name: "Polio (AFP)",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          vaccinationStatus: "N/A",
          actionTaken: "Surveillance active",
        },
        {
          name: "Tetanus (non-neonatal)",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          vaccinationStatus: "Unknown",
          actionTaken: "None",
        },
        {
          name: "Whooping Cough (Pertussis)",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          vaccinationStatus: "N/A",
          actionTaken: "None",
        },
        {
          name: "Diphtheria",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          vaccinationStatus: "N/A",
          actionTaken: "None",
        },
        {
          name: "Hepatitis B",
          suspected: 1,
          confirmed: 0,
          deaths: 0,
          vaccinationStatus: "Partial",
          actionTaken: "Contact tracing",
        },
        {
          name: "Mumps",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          vaccinationStatus: "N/A",
          actionTaken: "None",
        },
        {
          name: "Rubella",
          suspected: 0,
          confirmed: 0,
          deaths: 0,
          vaccinationStatus: "N/A",
          actionTaken: "None",
        },
      ],

      otherConditions: [
        {
          name: "Bilharzia (Schistosomiasis)",
          cases: 3,
          deaths: 0,
          trend: "Stable",
          remarks: "Praziquantel given",
        },
        {
          name: "Intestinal Worms",
          cases: 12,
          deaths: 0,
          trend: "Stable",
          remarks: "Albendazole distributed",
        },
        {
          name: "Scabies",
          cases: 5,
          deaths: 0,
          trend: "Decreasing",
          remarks: "Treatment given",
        },
        {
          name: "Dog Bites (Rabies exposure)",
          cases: 2,
          deaths: 0,
          trend: "Stable",
          remarks: "PEP initiated",
        },
        {
          name: "Snake Bites",
          cases: 1,
          deaths: 0,
          trend: "Stable",
          remarks: "Antivenom given",
        },
        {
          name: "Malnutrition (Severe Acute)",
          cases: 2,
          deaths: 0,
          trend: "Stable",
          remarks: "Referral to nutrition",
        },
      ],

      maternalTetanus: {
        cases: 0,
        deaths: 0,
      },

      neonatalTetanus: {
        cases: 0,
        deaths: 0,
      },

      afp: {
        cases: 0,
        adequateStool: 0,
        followUp: 0,
        pending: 0,
      },

      outbreaks: [
        {
          disease: "Measles",
          onsetDate: "2026-04-10",
          reportDate: "2026-04-12",
          cases: 3,
          deaths: 0,
          cfr: 0,
          status: "Contained",
        },
      ],

      chs: {
        activeCHVs: 12,
        householdsVisited: 245,
        eventsReported: 8,
      },

      feedback: {
        higherLevel:
          "Continue active surveillance for measles. Submit weekly reports on time.",
        recommendations:
          "Intensify case finding for AFP. Conduct community sensitization on cholera prevention.",
        challenges:
          "Limited laboratory supplies for sample collection. Transport delays for specimen referral.",
      },

      signOff: {
        compiledBy: "SR. JANE KAREGI MUNYUGI",
        designation: "Surveillance Focal Person",
        signature: "J.K. Munyugi",
        date: "2026-04-22",
        facilityIncharge: "DR. S. MWANGI",
      },
    };
  },
  computed: {
    reportingDate() {
      const today = new Date();
      return today.toLocaleDateString("en-GB");
    },
    weekStartDate() {
      // Simple calculation for demo - in real app would compute based on epi week
      const baseDate = new Date(this.selectedYear, 0, 1);
      const dayOffset = (this.selectedWeek - 1) * 7;
      const startDate = new Date(
        baseDate.setDate(baseDate.getDate() + dayOffset),
      );
      return startDate.toLocaleDateString("en-GB");
    },
    weekEndDate() {
      const baseDate = new Date(this.selectedYear, 0, 1);
      const dayOffset = (this.selectedWeek - 1) * 7 + 6;
      const endDate = new Date(
        baseDate.setDate(baseDate.getDate() + dayOffset),
      );
      return endDate.toLocaleDateString("en-GB");
    },
    totalAlerts() {
      const immediateAlerts = this.immediatelyNotifiable.filter(
        (d) => d.alertTriggered,
      ).length;
      const epidemicAlerts = this.epidemicProne.filter((d) => d.alertTriggered)
        .length;
      return immediateAlerts + epidemicAlerts;
    },
    totalConfirmed() {
      const immediate = this.immediatelyNotifiable.reduce(
        (sum, d) => sum + d.confirmed,
        0,
      );
      const epidemic = this.epidemicProne.reduce(
        (sum, d) => sum + d.confirmed,
        0,
      );
      return immediate + epidemic;
    },
    totalDeaths() {
      const immediate = this.immediatelyNotifiable.reduce(
        (sum, d) => sum + d.deaths,
        0,
      );
      const epidemic = this.epidemicProne.reduce((sum, d) => sum + d.deaths, 0);
      return immediate + epidemic;
    },
    totalOutbreaks() {
      return this.outbreaks.length;
    },
  },
  methods: {
    getTrendClass(trend) {
      if (trend === "Increasing") return "trend-up";
      if (trend === "Decreasing") return "trend-down";
      return "trend-stable";
    },
    getResponseClass(status) {
      if (status === "Contained") return "response-contained";
      if (status === "Ongoing") return "response-ongoing";
      return "response-investigating";
    },
    printPage() {
      window.print();
    },
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.report-container {
  background: white;
  min-height: 100vh;
  padding: 20px;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.header {
  text-align: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #1a56db;
}

.facility-name {
  font-size: 20px;
  font-weight: bold;
  color: #1a56db;
  margin-bottom: 6px;
}

.facility-details {
  font-size: 11px;
  color: #4b5563;
  line-height: 1.4;
  margin-bottom: 8px;
}

.report-title {
  font-size: 16px;
  font-weight: bold;
  background: #f3f4f6;
  display: inline-block;
  padding: 5px 16px;
  border-radius: 20px;
  color: #1f2937;
}

.report-subtitle {
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
}

.info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.info-left {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.info-value {
  font-size: 12px;
  color: #1f2937;
  font-weight: 500;
}

.week-select,
.year-input {
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
}

.btn-print {
  padding: 8px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.btn-print:hover {
  background: #2563eb;
}

.print-period {
  text-align: center;
  padding: 8px;
  margin-bottom: 20px;
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  font-size: 12px;
  color: #92400e;
  border-radius: 6px;
}

.summary-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.stat-box {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border-radius: 12px;
  padding: 12px 20px;
  text-align: center;
  min-width: 150px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #bfdbfe;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.stat-number {
  display: block;
  font-size: 26px;
  font-weight: bold;
  color: white;
}

.section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 14px;
  font-weight: bold;
  color: #1f2937;
  background: #e5e7eb;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.idsr-table,
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  min-width: 800px;
}

.idsr-table th,
.data-table th {
  background: #1f2937;
  color: white;
  padding: 8px 6px;
  font-weight: 600;
  text-align: center;
  border: 1px solid #374151;
}

.idsr-table td,
.data-table td {
  border: 1px solid #d1d5db;
  padding: 6px 4px;
  text-align: left;
}

.idsr-table td.text-center,
.data-table td.text-center {
  text-align: center;
}

.alert-badge {
  background: #fee2e2;
  color: #dc2626;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  display: inline-block;
}

.no-alert-badge {
  background: #d1fae5;
  color: #065f46;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  display: inline-block;
}

.stats-grid-2,
.stats-grid-3 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 10px;
}

.stat-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 16px;
  text-align: center;
}

.stat-card .stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #059669;
}

.stat-card .stat-label {
  font-size: 11px;
  font-weight: 600;
  color: #166534;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.trend-up {
  background: #fee2e2;
  color: #dc2626;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

.trend-down {
  background: #d1fae5;
  color: #065f46;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

.trend-stable {
  background: #fef3c7;
  color: #92400e;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

.response-contained {
  background: #d1fae5;
  color: #065f46;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

.response-ongoing {
  background: #fef3c7;
  color: #92400e;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

.response-investigating {
  background: #dbeafe;
  color: #1e40af;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

.no-data {
  text-align: center;
  padding: 20px;
  background: #f3f4f6;
  border-radius: 8px;
  color: #6b7280;
  font-size: 12px;
}

.feedback-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
}

.feedback-item {
  margin-bottom: 12px;
}

.feedback-item strong {
  font-size: 12px;
  color: #374151;
  display: block;
  margin-bottom: 4px;
}

.feedback-item p {
  font-size: 11px;
  color: #4b5563;
  line-height: 1.4;
}

.signoff-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.signoff-item {
  display: flex;
  flex-direction: column;
}

.signoff-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
}

.signoff-value {
  font-size: 12px;
  font-weight: 500;
  color: #1f2937;
}

@media print {
  @page {
    size: landscape;
    margin: 0.3cm;
  }

  .btn-print,
  .week-select,
  .year-input {
    display: none;
  }

  .info-bar {
    display: none;
  }

  .print-period {
    background: #fef3c7;
    margin: 10px 0;
    padding: 5px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .summary-stats {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .report-container {
    padding: 0.2cm;
  }

  .header {
    margin-bottom: 5px;
  }

  .facility-name {
    font-size: 14px;
  }

  .facility-details {
    font-size: 8px;
  }

  .report-title {
    font-size: 11px;
  }

  .section-title {
    font-size: 11px;
    padding: 4px 8px;
  }

  .idsr-table,
  .data-table {
    font-size: 8px;
  }

  .idsr-table th,
  .data-table th {
    padding: 4px 3px;
    font-size: 8px;
    background: #e5e7eb !important;
    color: black !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .idsr-table td,
  .data-table td {
    padding: 3px 2px;
    font-size: 7.5px;
  }

  .stat-card {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
